/* MUI */
import { Box, Button } from '@mui/material';

/*  Shared  */
import { Constants, ScopeTypes, URLs } from 'application/Shared/Constants';
import { Browser } from 'application/Shared/Handlers';
import { Helpers, SessionHelper } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  Images  */
import Image from 'images/Google_G_Logo.svg';

/*  Objects  */
import Session from 'objects/Session';

/*  React  */
import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';

import {
    AUTH_POPUP_WINDOW_FEATURES,
    AUTH_POPUP_WINDOW_TARGET
} from '../Constants';

/*  State  */
const state = () => ({
    loaded: false
});

class GoogleAuth extends PureComponent {
    constructor(props) {
        super(props);
        this.state = state();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = () => {
        this.setState({ loaded: true });
    };

    unload = () => {
        window.removeEventListener('storage', this.handleStorage);
    };

    handleSession = async () => {
        const { setSessionInfo } = this.props;

        const userSession = await Session.getSessionInfo();
        const sessionHelper = new SessionHelper(userSession);
        const tenant = sessionHelper.getTenant();
        setSessionInfo(userSession);
        Browser.handleTabTitle({ tenant });
    };

    handleStorage = (message) => {
        const { key, newValue } = message;

        if (key !== 'jwt') {
            return true;
        }

        if (newValue === 'NEW_TENANT') {
            window.removeEventListener('storage', this.handleStorage);
            window.location.href = URLs.waitlist;
            return true;
        }

        if (newValue && newValue !== 'null') {
            window.removeEventListener('storage', this.handleStorage);
            this.handleSession();
            return true;
        }

        debug.e('Unhandled Value', newValue);
        return true;
    };

    additionalParamsForScopeType = () => {
        const { scopeType } = this.props;
        const { googleAuth } = URLs;
        const {
            scopeUser,
            scopeGroup,
            scopeOrgUnit,
            scopeDomain,
            scopeProfile,
            scopeEmail,
            scopeReportAudit
        } = googleAuth;

        const additionalParams = {
            client_id: Constants.googleClientId,
            state: Constants.googleAuthState
        };

        if (scopeType === ScopeTypes.AppAuth) {
            additionalParams.scope = `${scopeUser} ${scopeGroup} ${scopeOrgUnit} ${scopeDomain} ${scopeReportAudit}`;
            additionalParams.state += '&scope=appAuth';
        }

        if (scopeType === ScopeTypes.Login) {
            additionalParams.scope = `${scopeProfile} ${scopeEmail}`;
        }
        return additionalParams;
    };

    handleLogin = () => {
        const { scopeType } = this.props;
        const { googleAuth } = URLs;
        const { endpoint } = googleAuth;

        const additionalPayloadParams = this.additionalParamsForScopeType();

        const params = Helpers.urlParams({
            // During the appAuth flow, when access_type is set to `offline`, we will get a refresh_token back from GWS on the first auth from an account.
            // the refresh_token is needed to generate new access tokens so we can continue to access the user's GWS data.
            // If we don't properly store the refresh_token on initial auth, we may not get it on subsequent auths.
            // But, according to this comment (https://github.com/googleapis/google-api-nodejs-client/issues/750#issuecomment-368873635),
            // if we set prompt to `consent`, then the user will see the consent screen again and a refresh_token will be returned.
            access_type:
                scopeType === ScopeTypes.AppAuth ? 'offline' : 'online',
            prompt:
                scopeType === ScopeTypes.AppAuth ? 'consent' : 'select_account',
            redirect_uri: `${window.origin}/close`,
            response_mode: 'query',
            response_type: 'code',
            ...additionalPayloadParams
        });

        const url = `${endpoint}?${params}`;
        window.open(url, AUTH_POPUP_WINDOW_TARGET, AUTH_POPUP_WINDOW_FEATURES);
        window.addEventListener('storage', (message) =>
            this.handleStorage(message)
        );
    };

    render() {
        const { login, t } = this.props;
        const { loaded } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Button
                onClick={this.handleLogin}
                color="primary"
                variant="outlined"
                style={{ width: '100%' }}
                data-testid="googleAuth"
                data-trackid="login_googleAuth"
            >
                <Box
                    display="flex"
                    width="100%"
                    alignItems="center"
                    justifyContent="center"
                >
                    {login ? (
                        <>
                            <Box width="30%" textAlign="right">
                                <img
                                    aria-hidden="true"
                                    src={Image}
                                    alt={t('Common.login_google')}
                                    style={{
                                        height: '20px',
                                        marginRight: '16px'
                                    }}
                                />
                            </Box>
                            <Box
                                width="70%"
                                textAlign="left"
                                textTransform="uppercase"
                            >
                                {t('Common.login_google')}
                            </Box>
                        </>
                    ) : (
                        <>
                            <img
                                aria-hidden="true"
                                src={Image}
                                alt={t('Common.login_google')}
                                style={{ height: '20px', marginRight: '16px' }}
                            />
                            <Box textTransform="uppercase">
                                {t('Common.login_google')}
                            </Box>
                        </>
                    )}
                </Box>
            </Button>
        );
    }
}

GoogleAuth.defaultProps = {
    scopeType: ScopeTypes.Login
};

export default withTranslation()(GoogleAuth);
